import { ConversationThreadDto, ThreadStatus } from '../dto/conversation-thread.dto';

export class ConversationThreadEntity {
  readonly id: string;
  readonly guestName: string;
  readonly guestEmail: string;
  readonly guestPhone: string | null;
  readonly propertyId: number;
  readonly propertyName: string | null;
  readonly objectTypeId: number | null;
  readonly status: ThreadStatus;
  readonly lastMessagePreview: string;
  readonly unreadCount: number;
  readonly checkIn: string;
  readonly checkOut: string;
  readonly numberOfGuests: number;
  readonly numberOfChildren: number | null;
  readonly numberOfPets: number | null;
  readonly petInfo: string | null;
  readonly totalPrice: number | null;
  readonly lastActivityAt: string;
  readonly createdAt: string;
  readonly updatedAt: string;

  /**
   * Human-readable relative time string computed at construction time.
   * TODO: This value is static and will become stale over time.
   * Consider adding periodic refresh if real-time accuracy is needed.
   */
  readonly relativeTime: string;

  constructor(dto: ConversationThreadDto) {
    this.id = dto.id;
    this.guestName = dto.guestName;
    this.guestEmail = dto.guestEmail;
    this.guestPhone = dto.guestPhone;
    this.propertyId = dto.propertyId;
    this.propertyName = dto.propertyName;
    this.objectTypeId = dto.objectTypeId;
    this.status = dto.status;
    this.lastMessagePreview = dto.lastMessagePreview;
    this.unreadCount = dto.unreadCount;
    this.checkIn = dto.checkIn;
    this.checkOut = dto.checkOut;
    this.numberOfGuests = dto.numberOfGuests;
    this.numberOfChildren = dto.numberOfChildren;
    this.numberOfPets = dto.numberOfPets;
    this.petInfo = dto.petInfo;
    this.totalPrice = dto.totalPrice;
    this.lastActivityAt = dto.lastActivityAt;
    this.createdAt = dto.createdAt;
    this.updatedAt = dto.updatedAt;

    this.relativeTime = this.computeRelativeTime();
  }

  get isUnread(): boolean {
    return this.unreadCount > 0;
  }

  get isClosed(): boolean {
    return this.status === 'closed';
  }

  get statusSeverity(): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    switch (this.status) {
      case 'new': return 'info';
      case 'open': return 'warn';
      case 'answered': return 'success';
      case 'closed': return 'secondary';
      default: return 'secondary';
    }
  }

  get messagePreview(): string {
    if (!this.lastMessagePreview) return '';
    return this.lastMessagePreview.length > 80
      ? this.lastMessagePreview.substring(0, 80) + '...'
      : this.lastMessagePreview;
  }

  get displayName(): string {
    return this.guestName || this.guestEmail;
  }

  get displayProperty(): string {
    return this.propertyName || `#${this.propertyId}`;
  }

  get hasBookingDetails(): boolean {
    return (this.numberOfChildren !== null && this.numberOfChildren > 0)
      || (this.numberOfPets !== null && this.numberOfPets > 0)
      || this.totalPrice !== null;
  }

  private computeRelativeTime(): string {
    const date = new Date(this.lastActivityAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return '<1m';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' });
  }
}
